import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { themeColours } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { Segmented } from '../../../components/primitives/Segmented';
import { useAccountId } from '../../../../../redux/slices/appInfo/hooks';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import { SpotifyHeaderFrame } from './SpotifyHeaderFrame';
import type { HeaderView } from './spotifyHeaderGeometry';
import { useArtistProfile } from './useArtistProfile';
import { spotifyHeaderMessages as messages } from './spotifyHeaderMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Spotify header; each part uses its own slice of it */
import styles from './SpotifyHeader.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SpotifyHeaderViewerMediaProps {
  /** The address of the made file. */
  src: string;
  label: string;
  /** The class of the plain file in the viewer. */
  fileClassName?: string;
}

/**
 * A made Spotify header in the asset viewer (design spotify-header a4): the file, or the file as
 * Spotify shows it on a desktop or a phone, with the name and the round photo of the artist. The
 * overlay is only in the viewer; the file has no name and no photo.
 */
const SpotifyHeaderViewerMediaComponent: React.FC<SpotifyHeaderViewerMediaProps> = ({ src, label, fileClassName }) => {
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const accountId = useAccountId();
  const profile = useArtistProfile(accountId ?? 0);
  const [view, setView] = useState<HeaderView>('desktop');
  // The file carries its own colours; the overlay needs none, so any theme gives the same picture.
  const colours = useMemo(() => themeColours('clean', []), []);
  const ground = useMemo(() => ({ kind: 'picture' as const, url: src, focus: { x: 0.5, y: 0.5 } }), [src]);
  const width = narrow ? 320 : view === 'phone' ? 420 : 760;

  return (
    <div className={styles.rail} data-testid="viewer-spotify-header">
      <Segmented
        ariaLabel={intl.formatMessage(messages.viewLabel)}
        value={view}
        onChange={setView}
        options={[
          { value: 'desktop', label: intl.formatMessage(messages.desktop) },
          { value: 'phone', label: intl.formatMessage(messages.phone) },
          { value: 'file', label: intl.formatMessage(messages.file) },
        ]}
      />
      {view === 'file' ? (
        <img className={fileClassName} src={src} alt={label} data-testid="viewer-image" />
      ) : (
        <SpotifyHeaderFrame
          width={width}
          view={view}
          ground={ground}
          colours={colours}
          overlay={{ artist: profile.name ?? '', avatarUrl: profile.avatarUrl }}
          testId="viewer-spotify-header-frame"
        />
      )}
    </div>
  );
};

export const SpotifyHeaderViewerMedia = TypedMemo(SpotifyHeaderViewerMediaComponent);
